import { ICreateProjectRequestParams } from "../../../domain/ICreateProjectRequestParams";
import { useAppDispatch } from "hooks/hooks";
import { useEffect } from "react";
import { addCreatedProject, setNewProjectPopoverOpen } from "../main.slice";
import {
  Control,
  Controller,
  FieldErrorsImpl,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetFocus
} from "react-hook-form";
import DatePicker from "react-datepicker";
import { FormatDateIso } from "utils/dateUtils";
import ProjectCategorySelect from "Dashboard/scenes/Projects/components/ProjectCategorySelect";
import ProjectTypeSelectCreate from "Dashboard/scenes/Projects/components/ProjectTypeSelectCreate";
import PartnerSimpleSelect from "Dashboard/scenes/Partners/components/PartnerSimpleSelect";
import { styles } from "../../../../../../components/Popover/popoverSelectStyle";
import { projectService } from "Dashboard/scenes/Projects/services/project.service";
import { toast } from "react-toastify";
import classes from "../../../../../../components/Popover/popover.module.scss";
import classesPr from "../../../styles/popover.module.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLongArrowAltDown } from "@fortawesome/free-solid-svg-icons";
import { IOption } from "Dashboard/domain/IOption";
import MunicipalitySelectCreate from "Dashboard/scenes/Partners/components/MunicipalitySelectCreate";

interface IProps {
  handleSubmit: UseFormHandleSubmit<ICreateProjectRequestParams>;
  register: UseFormRegister<ICreateProjectRequestParams>;
  getValues: UseFormGetValues<ICreateProjectRequestParams>;
  control: Control<ICreateProjectRequestParams, any>;
  clearErrors: UseFormClearErrors<ICreateProjectRequestParams>;
  reset: UseFormReset<ICreateProjectRequestParams>;
  errors: Partial<FieldErrorsImpl<ICreateProjectRequestParams>>;
  setFocus: UseFormSetFocus<ICreateProjectRequestParams>;
  clientPartnerValue: IOption | undefined;
  setClientPartnerValue: React.Dispatch<React.SetStateAction<IOption | undefined>>;
  municipalityValue: IOption | undefined;
  setMunicipalityValue: React.Dispatch<React.SetStateAction<IOption | undefined>>;
};

const NewProjectPopover = (props: IProps) => {
  const dispatch = useAppDispatch();

  const close = () => {
    resetForm();
    dispatch(setNewProjectPopoverOpen({ isOpen: false }));
  };

  const resetForm = () => {
    clearErrors();
    reset();
    props.setClientPartnerValue(undefined);
    props.setMunicipalityValue(undefined);
  };

  const {
    handleSubmit,
    register,
    getValues,
    control,
    clearErrors,
    reset,
    errors,
    setFocus,
  } = props;

  const onSubmit = async (data: ICreateProjectRequestParams) => {
    try {
      const createdProject = await projectService.createProject(data);
      dispatch(addCreatedProject(createdProject));
      toast.success("Projekat je uspešno dodat.");
      close();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    setFocus("projectName");
  }, [setFocus]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}
      className={`${classesPr.newProjectPopover} ${classes.basicPopover}`}
    >
      <div className={classes.row}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>projekat</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.projectName ? classes.itemError : ""}`}
            {...register("projectName", { required: true })} />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}
          ${errors.categoryId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>kategorija</label>
          <Controller
            name="categoryId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <ProjectCategorySelect
                value={getValues("categoryId")}
                customStyles={styles.customStyleClassic}
                error={errors.categoryId}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className={`${classes.col} ${classes.colDir}
          ${errors.typeId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>vrsta</label>
          <Controller
            name="typeId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <ProjectTypeSelectCreate
                value={getValues("typeId")}
                customStyles={styles.customStyleClassic}
                error={errors.typeId}
                onChange={onChange}
                preserveInput={true}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}
          ${errors.clientPartnerId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>naručilac</label>
          <Controller
            name="clientPartnerId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <PartnerSimpleSelect
                value={props.clientPartnerValue}
                setValue={props.setClientPartnerValue}
                customStyles={styles.customStyleClassic}
                error={errors.clientPartnerId}
                onChange={onChange}
              />
            )}
          />
        </div>
        <div className={`${classes.col} ${classes.colDir}
          ${errors.municipalityId ? classes.itemError : ""}`}>
          <label className={classes.aboveLabel}>mesto</label>
          <Controller
            name="municipalityId"
            control={control}
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange } }) => (
              <MunicipalitySelectCreate
                value={props.municipalityValue}
                setValue={props.setMunicipalityValue}
                customStyles={styles.customStyleClassic}
                error={errors.municipalityId}
                onChange={onChange}
                preserveInput={true}
              />
            )}
          />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena doručka</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.breakfastCost ? classes.itemError : ""}`}
            {...register("breakfastCost", { required: true })} />
        </div>

        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena keš doručka</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.cashBreakfastRate ? classes.itemError : ""}`}
            {...register("cashBreakfastRate", { required: true })} />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena ručka</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.lunchCost ? classes.itemError : ""}`}
            {...register("lunchCost", { required: true })} />
        </div>

        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena keš ručka</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.cashLunchRate ? classes.itemError : ""}`}
            {...register("cashLunchRate", { required: true })} />
        </div>
      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena večere</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.dinnerCost ? classes.itemError : ""}`}
            {...register("dinnerCost", { required: true })} />
        </div>

        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>cena keš večere</label>
          <input
            type="text"
            className={`${classes.itemBorder}
              ${errors.cashDinnerRate ? classes.itemError : ""}`}
            {...register("cashDinnerRate", { required: true })} />
        </div>      </div>

      <div className={`${classes.row} ${classes.rowDir}`}>
        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>datum ugovora</label>
          <Controller
            control={control}
            name="contractDate"
            defaultValue={undefined}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                onChange={(date) =>
                  onChange(date ? FormatDateIso(date) : undefined)
                }
                showMonthDropdown
                showYearDropdown
                dateFormat="dd.MM.yyyy"
                selected={value ? new Date(value) : null}
                className={`${classes.dateItem} ${classes.itemBorder}
                  ${errors.contractDate ? classes.itemError : ""}`}
              />
            )}
          />
        </div>

        <div className={`${classes.col} ${classes.colDir}`}>
          <label className={classes.aboveLabel}>rok za završetak</label>
          <Controller
            control={control}
            name="deadlineForCompletion"
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                onChange={(date) =>
                  onChange(date ? FormatDateIso(date) : undefined)
                }
                showMonthDropdown
                showYearDropdown
                dateFormat="dd.MM.yyyy"
                selected={value ? new Date(value) : null}
                className={`${classes.dateItem} ${classes.itemBorder}
                  ${errors.deadlineForCompletion ? classes.itemError : ""}`}
              />
            )}
          />
        </div>
      </div>


      <div className={classes.row}>
        <div className={classes.buttons}>
          <button className={classes.iconButton} type="submit">
            <FontAwesomeIcon
              icon={faLongArrowAltDown}
              className={classes.icon} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default NewProjectPopover;
